import styles from "./styles.module.scss";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import secondaryComponents from "../../../../components/secondaryComponents";
import primaryComponents from "../../../../components/primaryComponents";
import { motion } from "framer-motion";
import useGeneralLedger from "./useGeneralLedger";
import { formatCurrency, accountingSources } from "../../../../helpers";
import { useSelector } from "react-redux";
import { useStoreCurrency } from "../../../../hooks/useStoreCurrency";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const GeneraLedger = () => {
  const {
    storeList,
    chartAccountsList,
    isLoadingChartAcountsList,
    chartAccountTransactions,
    isLoadingChartAccountTransactions,
    generalLedgerSummary,
    isLoadingGeneralLedgerSummary,
    startDate,
    endDate,
    currentStore,
    metaDetails,
    entrySource,
    showExportAccountStatement,
    selectedChartAccount,
    filterByChartAccount,
    setSelectedChartAccount,
    gotoPage,
    setDateFilter,
    handleFetchChartAccountTransactions,
    filterByStoreId,
    setEntrySourceFilter,
    setShowExportAccountStatement,
  } = useGeneralLedger();
  const { storeHasMultipleCurrency } = useStoreCurrency();
  const { currency_code } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | General Ledger"
      description="View and manage your general ledger, including transactions, balances, and account details"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          className={` ${globalStyles.pageColumnGaps}`}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center mt-2">
            <div>
              <h4 className="font-weight-semibold">General Ledger</h4>
              <p className="default-text-size">
                View and manage your general ledger, including transactions,
                balances, and account details
              </p>
            </div>
          </div>

          <div className={`${styles.card} mt-3`}>
            <div className="d-flex flex-wrap justify-content-between w-100">
              <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
                <secondaryComponents.LoaderHelper
                  isLoading={isLoadingChartAcountsList}
                  classNames="mt-1"
                >
                  <div style={{ width: "350px" }}>
                    <primaryComponents.SelectField
                      options={chartAccountsList?.map((account) => ({
                        id: account?.id,
                        name: account?.account_label,
                      }))}
                      idKey="id"
                      nameKey="name"
                      value={
                        chartAccountsList
                          ? chartAccountsList?.[0]?.account_label
                          : "Select Bank"
                      }
                      onChange={(value) => {
                        setSelectedChartAccount(value);
                        filterByChartAccount(value);
                      }}
                      floatError={false}
                    />
                  </div>
                </secondaryComponents.LoaderHelper>
                <primaryComponents.DateDefinedRangeInput
                  startDate={startDate}
                  endDate={endDate}
                  onChange={setDateFilter}
                />
                <div style={{ width: "200px" }}>
                  <primaryComponents.SelectField
                    options={
                      storeHasMultipleCurrency
                        ? [...storeList]
                        : [
                            {
                              name: "All stores",
                              id: "ALL_STORES",
                            },
                            ...storeList,
                          ]
                    }
                    idKey="id"
                    nameKey="name"
                    onChange={(value) => {
                      filterByStoreId(value);
                    }}
                    value={
                      storeList?.find(
                        (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                      )?.name || "All stores"
                    }
                    floatError={false}
                    classNames="store-select"
                  />
                </div>
                <div className="ml-auto" style={{ width: "200px" }}>
                  <primaryComponents.SelectField
                    options={accountingSources}
                    idKey="id"
                    nameKey="name"
                    onChange={(value) => {
                      setEntrySourceFilter(value);
                    }}
                    value={
                      accountingSources?.find(
                        (accountingSource) =>
                          `${accountingSource?.id}` === `${entrySource}`,
                      )?.name || "All entry types"
                    }
                    floatError={false}
                  />
                </div>
              </div>
              <div>
                <primaryComponents.Button
                  classNames="btn btn--outline"
                  onClick={() => setShowExportAccountStatement(true)}
                >
                  <FontAwesomeIcon icon="download" className="mr-2" />
                  Export
                </primaryComponents.Button>
              </div>
            </div>
          </div>
          <secondaryComponents.LoaderHelper
            isLoading={isLoadingGeneralLedgerSummary}
            classNames={"mt-5"}
          >
            <div className={`${styles.accountDetails}`}>
              <div className={styles.accountDetails__accountName}>
                <span>Account</span>
                <span>{generalLedgerSummary?.account_name}</span>
                <span>
                  {generalLedgerSummary?.account_code} {" - "}
                  {generalLedgerSummary?.sub_account_type_label}
                </span>
              </div>
              <div className={styles.accountDetails__balance}>
                <span>Opening Balance</span>
                <span className="text-primary">
                  {formatCurrency({
                    value: Math.abs(generalLedgerSummary?.opening_balance || 0),
                    currencyCode: currency_code,
                  })}
                  {generalLedgerSummary?.opening_balance > 0
                    ? " Dr"
                    : generalLedgerSummary?.opening_balance < 0
                      ? " Cr"
                      : ""}
                </span>
              </div>
              <div className={styles.accountDetails__balance}>
                <span>Total Debit</span>
                <span>
                  {formatCurrency({
                    value: generalLedgerSummary?.total_debits || "0",
                    currencyCode: currency_code,
                  })}
                </span>
              </div>
              <div className={styles.accountDetails__balance}>
                <span>Total Credit</span>
                <span>
                  {formatCurrency({
                    value: generalLedgerSummary?.total_credits || "0",
                    currencyCode: currency_code,
                  })}
                </span>
              </div>
              <div className={styles.accountDetails__balance}>
                <span>Running Balance</span>
                <span className="text-primary">
                  {formatCurrency({
                    value: Math.abs(generalLedgerSummary?.running_balance || 0),
                    currencyCode: currency_code,
                  })}
                  {generalLedgerSummary?.running_balance > 0
                    ? " Dr"
                    : generalLedgerSummary?.running_balance < 0
                      ? " Cr"
                      : ""}
                </span>
              </div>
            </div>
          </secondaryComponents.LoaderHelper>
          {/* table session */}
          <div className={styles.tableBody}>
            <div
              className={`m-1 d-flex justify-content-end ${styles.tableBody__header}`}
            ></div>

            <secondaryComponents.LoaderHelper
              isLoading={isLoadingChartAccountTransactions}
              classNames={"mt-5"}
            >
              <secondaryComponents.GeneralLedgerTable
                banks={chartAccountTransactions}
                handleFetchbanks={handleFetchChartAccountTransactions}
                gotoPage={gotoPage}
                metaDetails={metaDetails}
              />
            </secondaryComponents.LoaderHelper>
          </div>
        </motion.div>
        <secondaryComponents.SideModal
          isActive={showExportAccountStatement}
          closeModal={() => setShowExportAccountStatement(false)}
        >
          <secondaryComponents.AccountStatementExport
            closeWidget={() => setShowExportAccountStatement(false)}
            chartAccountId={selectedChartAccount}
          />
        </secondaryComponents.SideModal>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};

export default GeneraLedger;
